/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { __ } from '@web-stories-wp/i18n';
import { useContext, useEffect, useRef } from '@web-stories-wp/react';
import styled from 'styled-components';

/**
 * Internal dependencies
 */
import { Panel, PanelTitle, PanelContent, panelContext } from '../../panel';
import useInspector from '../../../inspector/useInspector';
import { LAYER_HEIGHT, DEFAULT_LAYERS_VISIBLE } from './constants';
import LayerList from './layerList';
import useLayers from './useLayers';

const Container = styled.div`
  margin: 0 4px;
  filter: drop-shadow(0px -4px 8px rgba(0, 0, 0, 0.2));
`;

const StyledPanelTitle = styled(PanelTitle)`
  height: 64px;
  border-top-left-radius: ${({ theme }) => theme.borders.radius.small};
  border-top-right-radius: ${({ theme }) => theme.borders.radius.small};
`;

const DividerContainer = styled.div`
  background: ${({ theme }) => theme.colors.bg.tertiary};
`;

const Divider = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider.quarternary};
`;

function LayerPanelAutoResize({ isReady, targetHeight }) {
  const {
    state: { height, isCollapsed, maxHeight: panelMaxHeight },
    actions: { adjustHeight, setExpandToHeight },
  } = useContext(panelContext);
  const lastAppliedTargetHeight = useRef();

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (lastAppliedTargetHeight.current === undefined) {
      lastAppliedTargetHeight.current = targetHeight;
      return;
    }
    if (
      targetHeight === lastAppliedTargetHeight.current ||
      panelMaxHeight < targetHeight
    ) {
      return;
    }
    setExpandToHeight(targetHeight);

    if (!isCollapsed) {
      if (
        targetHeight > lastAppliedTargetHeight.current ||
        targetHeight < height
      ) {
        adjustHeight(targetHeight);
      }
    }

    lastAppliedTargetHeight.current = targetHeight;
  }, [
    adjustHeight,
    isCollapsed,
    isReady,
    height,
    panelMaxHeight,
    setExpandToHeight,
    targetHeight,
  ]);

  return null;
}

function LayerPanel() {
  const { layers, handleDragPosition, visibleLayers } = useLayers();
  const {
    state: { inspectorContentHeight },
  } = useInspector();

  const initialNumVisibleLayers = Math.min(
    visibleLayers.length,
    DEFAULT_LAYERS_VISIBLE
  );
  const contentHeight = visibleLayers.length * LAYER_HEIGHT;
  const inspectorMaxHeight = inspectorContentHeight
    ? Math.round(inspectorContentHeight * 0.7)
    : null;
  const maxHeight = inspectorMaxHeight
    ? Math.min(contentHeight, inspectorMaxHeight)
    : contentHeight;
  const isReady = layers.length > 0;

  return (
    <Container>
      <Panel
        name="layers"
        initialHeight={Math.min(
          initialNumVisibleLayers * LAYER_HEIGHT,
          window.innerHeight / 3
        )}
        maxHeight={maxHeight}
        resizeable
        ariaHidden
        collapsedByDefault={false}
        isReady={isReady}
      >
        <LayerPanelAutoResize isReady={isReady} targetHeight={maxHeight} />
        <StyledPanelTitle isSecondary isResizable>
          {__('Layers', 'web-stories')}
        </StyledPanelTitle>

        <DividerContainer>
          <Divider />
        </DividerContainer>

        <PanelContent isSecondary padding="0">
          <LayerList layers={layers} handleDragPosition={handleDragPosition} />
        </PanelContent>
      </Panel>
    </Container>
  );
}

export default LayerPanel;
